// #useAuthUpdated

import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import { supabase } from "../../../../../supabase";
import { useAuth } from "../../../../../contexts/AuthContext";
import SearchToolbar from "./SearchToolbar";
import LinksList from "./LinksList";
import { AddSupplierLinkModal } from "./AddSupplierLinkModal";

/**
 * Component that renders a list of supplier links for a given supplier ID.
 * It fetches the links from Supabase and renders them in a grid.
 * If the user is an admin, it will also render an "Add new link" button.
 * @param {object} props - The props object.
 * @param {string} props.supplierId - The ID of the supplier to fetch links for
 */
function SupplierLinks({ supplierId }) {
  // States
  const [linksArr, setLinksArr] = useState([]);
  const [filteredLinks, setFilteredLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [page, setPage] = useState(1);

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);

  const { currentUser } = useAuth();

  // Check if user is an admin
  const userAdmin = currentUser.is_admin;

  // Rest options for link
  const restVar = {
    DEALER_CODE: `Z-${currentUser.id}`,
    DEALER_NAME: currentUser.company_name,
    ZAWADI_DEALER_CODE: `zawadi_Z-${currentUser.id}`,
  };

  // Function to fetch supplier links from Supabase
  const fetchSupplierLinks = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("suppliers_link_index")
        .select("*")
        .eq("supplier_id", supplierId)
        .eq("is_active", true);

      if (error) throw error;

      const enrichedLinks = data.map(link => ({
        ...link,
      }));

      setLinksArr(enrichedLinks);
      setFilteredLinks(enrichedLinks);
    } catch (error) {
      console.error("Error fetching supplier links:", error);
    } finally {
      setLoading(false);
    }
  };

  // Apply search filter when search query or links array changes
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredLinks(linksArr);
    } else {
      const filtered = linksArr.filter(
        link =>
          link.link_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          link.link_address.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredLinks(filtered);
    }
    setPage(1); // Reset to first page when filtering
  }, [searchQuery, linksArr]);

  useEffect(() => {
    fetchSupplierLinks();
  }, [supplierId, currentUser.id]);

  // Function to add a supplier link
  const addSupplierLink = async newLink => {
    try {
      setLoading(true);
      const { error } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("suppliers_link_index")
        .upsert({
          ...newLink,
          supplier_id: supplierId,
          created_by: currentUser.id,
        });

      if (error) throw error;
      fetchSupplierLinks();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Error adding supplier link:", error);
    } finally {
      setLoading(false);
    }
  };

  // Function to edit a supplier link
  const editSupplierLink = async (linkId, updatedLink) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("suppliers_link_index")
        .update(updatedLink)
        .eq("id", linkId);

      if (error) throw error;
      fetchSupplierLinks();
      setEditModalOpen(false);
      setSelectedLink(null);
    } catch (error) {
      console.error("Error updating supplier link:", error);
    } finally {
      setLoading(false);
    }
  };

  // Function to remove a supplier link
  const removeSupplierLink = async () => {
    if (!selectedLink) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("suppliers_link_index")
        .update({ is_active: false })
        .eq("id", selectedLink.id);

      if (error) throw error;
      fetchSupplierLinks();
      setDeleteModalOpen(false);
      setSelectedLink(null);
    } catch (error) {
      console.error("Error removing supplier link:", error);
    } finally {
      setLoading(false);
    }
  };

  // Function to open a link and record usage
  const handleOpenLink = link => {
    // Build link address with REST variables
    const linkAddress = [link.link_address];

    if (link.rest_var && link.rest_var[0]) linkAddress.push("?");

    if (link.rest_var) {
      for (let i = 0; i < link.rest_var.length; i++) {
        linkAddress.push(
          `${link.rest_var[i].q}=${
            link.rest_var[i].is_static
              ? link.rest_var[i].var
              : restVar[link.rest_var[i].var]
          }${i === link.rest_var.length - 1 ? "" : "&"}`
        );
      }
    }

    window.open(linkAddress.join(""), "_blank");

    // Update displayed links
    fetchSupplierLinks();
  };

  // Handle edit button click
  const handleEditLink = link => {
    setSelectedLink(link);
    setEditModalOpen(true);
  };

  // Handle delete button click
  const handleDeleteLink = link => {
    setSelectedLink(link);
    setDeleteModalOpen(true);
  };

  return (
    <Container maxWidth="xl">
      {/* Section Title */}
      <Box mb={3}>
        <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 8 }}>
          Supplier Links
        </Typography>
        <Typography style={{ color: "#666", marginBottom: 24 }}>
          Manage all your supplier links in one place
        </Typography>
      </Box>

      {/* Search Toolbar */}
      <SearchToolbar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        viewMode={viewMode}
        setViewMode={setViewMode}
        userAdmin={userAdmin}
        onAddClick={() => {
          setSelectedLink(null);
          setEditModalOpen(true);
        }}
      />

      {/* Links List */}
      <LinksList
        links={filteredLinks}
        loading={loading}
        userAdmin={userAdmin}
        page={page}
        setPage={setPage}
        viewMode={viewMode}
        onOpenLink={handleOpenLink}
        onEditLink={handleEditLink}
        onDeleteLink={handleDeleteLink}
      />

      {/* Edit Modal */}
      {editModalOpen && (
        <Dialog
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedLink(null);
          }}
          maxWidth="md"
        >
          <AddSupplierLinkModal
            restVar={restVar}
            addNewLink={linkData => {
              if (selectedLink) {
                editSupplierLink(selectedLink.id, linkData);
              } else {
                addSupplierLink(linkData);
              }
            }}
            onClose={() => {
              setEditModalOpen(false);
              setSelectedLink(null);
            }}
            supLink={
              selectedLink || {
                link_name: "",
                link_address: "",
                is_active: true,
                rest_var: [],
              }
            }
            edit={!!selectedLink}
          />
        </Dialog>
      )}

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedLink(null);
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedLink?.link_name}"? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteModalOpen(false);
              setSelectedLink(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={removeSupplierLink} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default SupplierLinks;
